import { withManifest } from '@godaddy/gasket-hcs';
import ProductNav from '@ux/product-nav';
import UtilityBar from '@ux/utility-bar';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { withCustomerFromCookie } from './withCustomerFromCookie';
import { syncInfoIdpCookie, removeInfoIdpCookie, isSecureServerDomain } from '../lib/utils/cookie-sync';
import { getStorefrontUrl } from '../lib/utils/get-storefront-url';
import { HivemindContext } from '@ux/hivemind-provider';

import '@ux/browser-deprecation-banner/styles';
import '@ux/utility-bar/styles';
import '@ux/product-nav/styles';

import './header.css';

// css for a11y skip to main component
import '@godaddy/gasket-hcs/dist/styles.css';

// adapted from legacy sales-header and utility-header transform.js files
// ticket to extract this function to header-utils: https://godaddy-corp.atlassian.net/browse/UXP-8204
/**
   * Orders market data from all function retrieval
   *
   * @param {Object} marketData Market data
   * @returns {Object} Ordered market data
   * @private
   */
export const orderMarketData = (marketData) => {
  const orderedMarketData = Object.keys(marketData) // ar-ae
    .filter(market => !marketData[market].internal)
    .sort((a, b) => {
      const lmarket = marketData[a];
      const rmarket = marketData[b];

      //
      // Use the country code to compare the countryName
      // because those are not guaranteed to be equal.
      // (e.g. België - Nederlands and Belgique - Français)
      //
      if (lmarket.countryCode === rmarket.countryCode) {
        //
        // If the languageName is also identical, it is the same.
        // If they are both not the primary language, sort by languageName
        // ensuring locale awareness.
        //
        if (lmarket.languageName === rmarket.languageName) { return 0; }
        if (!lmarket.primaryLanguageForCountry && !rmarket.primaryLanguageForCountry) {
          return (lmarket.languageName || '').localeCompare(rmarket.languageName);
        }

        //
        // Ensure the primary language is always higher in the list
        //
        if (lmarket.primaryLanguageForCountry) { return -1; }
        if (rmarket.primaryLanguageForCountry) { return 1; }
      }

      //
      // If they are different countries, sort by countryName
      // ensuring locale awareness.
      //
      return (lmarket.countryName || '').localeCompare(rmarket.countryName);
    }).reduce((acc, market) => {
      acc[market] = marketData[market];
      return acc;
    }, {});
  return orderedMarketData;
};

/**
 * React Component to render the Header.
 * This component must be present, but may return null if no header is required.
 *
 * @param {Object} props The props for this component
 * @returns {React.ReactElement} JSX for header
 */
export function HeaderRootBase({ marketData = {}, ...props }) {
  const { requestedHeader, customer, CartComponent } = props;
  const [cartProps, setCartProps] = React.useState({
    messages: props.messages || {},
    checkout: props.cartUrl || '',
    items: props.cartItems || customer?.items || 0
  });

  const components = {
    utilityBar: {
      accountTray: true,
      blog: false,
      cart: true,
      contactTray: true,
      help: true,
      logo: true,
      marketSelector: true,
      notifications: true,
      utilitySeparator: false,
      accountDelegation: false // don't use account delegation from utility-bar, we use it from the WithManifest HOC
    },
    productNav: {
    }
  };

  const { ready, client } = React.useContext(HivemindContext);
  if (ready) {
    const experimentId = '123_hide_contact_number_AB';
    const experimentCohorts = client.getCohorts({
      experiments: [experimentId],
      attributes: {
        app: props.app,
        privateLabelId: props.privateLabelId,
        requestedHeader
      }
    });

    if (experimentCohorts?.cohorts?.[experimentId]?.cohortId === 'b_variant') {
      components.utilityBar.contactTray = false;
    }
  }

  React.useEffect(() => {
    // use the items count from the extensibility function if available otherwise use the customer items count
    const items = props.cartItems || customer?.items || 0;

    // use the checkout url from the extensibility function if available otherwise use the default url
    const checkoutUrl = props.cartUrl || props.urls?.checkout?.href || '';

    let prefix = 'uxp.hyd.sales_header.';
    let cartEid = 'cart_empty.link.click';
    if (props.seechange) {
      prefix = 'uxp.hyd.sales_header_seechange.';
    }
    if (items > 0) {
      cartEid = 'cart_full.link.click';
    }
    cartEid = prefix + cartEid;

    setCartProps({
      items,
      market: 'en-US',
      messages: props.messages,
      checkout: checkoutUrl,
      eid: cartEid,
      className: 'cart-link',
      showEmpty: true,
      textOnly: false
    });
  }, [props.seechange, props.messages, props.cartItems, props.cartUrl, customer?.items, props.urls?.checkout?.href]);

  const orderedMarketData = React.useMemo(() => {
    return orderMarketData(marketData);
  }, [marketData]);

  return <>
    <UtilityBar
      { ...props }
      { ...customer }
      components={ components.utilityBar }
      preset={ requestedHeader }
      marketData={ orderedMarketData }
      marketSelector={{
        markets: Object.keys(orderedMarketData)
      }}
      isReseller={ true }
      cart={ cartProps }
      manifest='SalesHeader'
      CartComponent={ CartComponent }
    />
    <ProductNav
      { ...props }
      isReseller={ true }
      components={ components.productNav }
      navigation={ props.productNavigation ? props.productNavigation : [] }
    />
  </>;
}

// Wrap HeaderRoot with customer cookie HOC
export const HeaderRoot = withCustomerFromCookie(HeaderRootBase);

export function Header(props) {
  const [CartComponent, setCartComponent] = useState(null);
  const [cartItems, setCartItems] = useState(0);
  const [cartUrl, setCartUrl] = useState('');

  // Sync info_idp cookie on component mount (client-side only)
  useEffect(() => {
    const syncCookie = async () => {
      // Only run on client-side
      if (typeof window === 'undefined') {
        return;
      }

      try {
        // Use environment from props, or fallback to 'development'
        const storefrontDomain = getStorefrontUrl(props.environment || props.env);

        await syncInfoIdpCookie(storefrontDomain);

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to sync info_idp cookie:', error);
      }
    };

    syncCookie();
  }, [props.environment, props.env]);

  // Set up event listener to remove info_idp cookie on logout
  useEffect(() => {
    // Only run on client-side
    if (typeof window === 'undefined' || isSecureServerDomain()) {
      return;
    }

    const handleLogoutClick = (event) => {
      // Prevent default navigation
      event.preventDefault();

      // Remove the cookie
      removeInfoIdpCookie();

      // Get the logout URL
      const logoutUrl = event.currentTarget.href || event.currentTarget.getAttribute('href');

      // Navigate to logout after a small delay to ensure cookie is removed
      setTimeout(() => {
        window.location.href = logoutUrl;
      }, 100);
    };

    // Find the logout link element and attach listener directly to it
    const attachLogoutListener = () => {
      const logoutLink = document.querySelector('a.logout-link');
      if (logoutLink) {
        logoutLink.addEventListener('click', handleLogoutClick);
        return logoutLink;
      }
      return null;
    };

    // Try to attach listener immediately
    let logoutLink = attachLogoutListener();

    // If not found, set up a MutationObserver to watch for when it's added to the DOM
    let observer = null;
    if (!logoutLink) {
      observer = new MutationObserver(() => {
        if (!logoutLink) {
          logoutLink = attachLogoutListener();
          if (logoutLink && observer) {
            observer.disconnect();
          }
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    // Cleanup function to remove event listener and observer
    return () => {
      if (logoutLink) {
        logoutLink.removeEventListener('click', handleLogoutClick);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  /**
   * Helper to update cart component.
   *
   * @param {React.ReactElement} component Updated React component.
   * @param {Function} done Continuation to call when complete.
   * @api public
   */
  const updateCartComponent = useCallback((component, done = () => { }) => {
    setCartComponent(typeof component === 'function' ? () => component : component);
    done();
  }, [setCartComponent]);

  /**
   * Helper to update cart items.
   *
   * @param {number} cartItems Updated cart count.
   * @param {function} done Optional completion callback.
   * @public
   */
  const updateCart = useCallback((cartItems, done = () => { }) => {
    const newCartItems = parseInt(cartItems, 10) || 0;
    setCartItems(newCartItems);
    done();
  }, []);

  /**
   * Helper to update cart url
   *
   * @param {string} url Updated cart url
   * @param {function} done Optional completion callback
   * @public
   */
  const updateCartUrl = useCallback((url, done = () => { }) => {
    setCartUrl(url);
    done();
  }, [setCartUrl]);

  const headerMethods = useMemo(() => ({
    updateCartComponent,
    updateCart,
    updateCartUrl
  }), [updateCartComponent, updateCart, updateCartUrl]);

  const HeaderComponent = useMemo(() => withManifest(HeaderRoot, {
    initCustomerState: true,
    initTraffic: true,
    additionalHeaderMethods: headerMethods,
    componentName: 'header',
    renderAccountDelegation: true
  }), [headerMethods]);

  return (<HeaderComponent { ...props } CartComponent={ CartComponent } cartItems={ cartItems } cartUrl={ cartUrl }/>);
}

Header.displayName = 'Header';

/**
 * Export Header with Manifest HOC.
 *
 * @component
 * @public
 */
export default Header;

